import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface PageH1RouteData {
  pageHeadingKey?: string;
  pageType?: PageType;
  indexable?: boolean;
}

export type PageType =
  | 'home'
  | 'disaster'
  | 'location'
  | 'organization'
  | 'org-disaster'
  | 'org-location'
  | 'static';

const PAGE_TYPE_FALLBACK_KEYS: Record<PageType, string> = {
  home: 'PAGE_HEADING-HOME',
  disaster: 'PAGE_HEADING-FALLBACK-DISASTER',
  location: 'PAGE_HEADING-FALLBACK-LOCATION',
  organization: 'PAGE_HEADING-FALLBACK-ORGANIZATION',
  'org-disaster': 'PAGE_HEADING-FALLBACK-ORG_DISASTER',
  'org-location': 'PAGE_HEADING-FALLBACK-ORG_LOCATION',
  static: 'PAGE_HEADING-FALLBACK-STATIC',
};

interface ComponentHeading {
  text?: string;
  key?: string;
  params?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class PageH1Service {
  private readonly headingSubject = new BehaviorSubject<string | null>(null);
  private readonly indexableSubject = new BehaviorSubject<boolean>(false);
  private readonly isHomePageSubject = new BehaviorSubject<boolean>(false);

  private componentHeading: ComponentHeading | null = null;
  private routeHeadingKey: string | null = null;
  private routePageType: PageType | null = null;
  private activeRouteUrl = '';

  readonly heading$ = this.headingSubject.asObservable();
  readonly isHomePage$ = this.isHomePageSubject.asObservable();

  constructor(private router: Router, private translateService: TranslateService) {}

  initialize(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.activeRouteUrl = event.urlAfterRedirects;
      this.componentHeading = null;
      this.applyRouteHeading();
    });

    this.translateService.onLangChange.subscribe(() => {
      this.publishHeading();
    });

    this.activeRouteUrl = this.router.url;
    this.applyRouteHeading();
  }

  setHeading(heading: string | null | undefined): void {
    this.componentHeading = { text: heading?.trim() || undefined };
    this.publishHeading();
  }

  setHeadingKey(key: string, params?: Record<string, unknown>): void {
    this.componentHeading = { key, params };
    this.publishHeading();
  }

  clearComponentHeading(): void {
    this.componentHeading = null;
    this.publishHeading();
  }

  private applyRouteHeading(): void {
    const routeData = this.getMergedRouteData(this.router.routerState.root);
    const indexable = (routeData.indexable ?? true) && !this.isNonIndexableUrl(this.router.url);

    this.indexableSubject.next(indexable);
    this.routeHeadingKey = routeData.pageHeadingKey || null;
    this.routePageType = routeData.pageType || null;

    const path = this.router.url.split('?')[0].split('#')[0];
    this.isHomePageSubject.next(path === '' || path === '/');

    if (!indexable) {
      this.headingSubject.next(null);
      return;
    }

    this.publishHeading();
  }

  private publishHeading(): void {
    if (!this.indexableSubject.value) {
      this.headingSubject.next(null);
      return;
    }

    if (this.componentHeading) {
      if (this.componentHeading.text) {
        this.headingSubject.next(this.componentHeading.text);
        return;
      }

      if (this.componentHeading.key) {
        this.headingSubject.next(
          this.translateService.instant(this.componentHeading.key, this.componentHeading.params)
        );
        return;
      }
    }

    if (this.routeHeadingKey) {
      this.headingSubject.next(this.translateService.instant(this.routeHeadingKey));
      return;
    }

    if (this.routePageType) {
      this.headingSubject.next(this.translateService.instant(PAGE_TYPE_FALLBACK_KEYS[this.routePageType]));
      return;
    }

    this.headingSubject.next(this.getFallbackHeading());
  }

  private getFallbackHeading(): string {
    const path = this.activeRouteUrl.split('?')[0].split('#')[0];
    const segments = path
      .split('/')
      .filter(Boolean)
      .filter((segment) => !/^\d+$/.test(segment) && !/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment));
    const heading = segments[segments.length - 1] || 'Page';

    try {
      return decodeURIComponent(heading)
        .replace(/[-_.]+/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    } catch (_error) {
      return heading.replace(/[-_.]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
    }
  }

  /** Merge route data from the full activated route tree (parent + lazy child). */
  private getMergedRouteData(route: ActivatedRoute): PageH1RouteData {
    let merged: PageH1RouteData = {};
    let current: ActivatedRoute | null = route;

    while (current) {
      merged = { ...merged, ...(current.snapshot.data as PageH1RouteData) };
      current = current.firstChild;
    }

    return merged;
  }

  private isNonIndexableUrl(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];

    return (
      path.startsWith('/dashboard') ||
      path === '/givelively-donation' ||
      path === '/disaster-about' ||
      path === '/location-about'
    );
  }
}
